import React, { FC, useMemo, useState } from 'react';
import { DesktopDatePicker, DesktopDatePickerProps, LocalizationProvider } from '@mui/x-date-pickers';
import { validateInput } from '../../utils/validators/Validators';

import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { identifiers } from '../../utils/constants/identifiers';
import { FormValidators } from '../Form';
import { TextField, useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/pt';
import 'dayjs/locale/en';
import strings from 'common/Translation/Translate';
import { useSelector } from 'react-redux';
import { defaultLanguage } from 'utils/redux/reducer/authentication-slice';

dayjs.extend(customParseFormat);

export interface DateInputProps {
  field?: string;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  typeValue?: string;
  textChange: (value: string, field: string, error?: { error: boolean; message: string }) => void;
  validators?: FormValidators[];
  hasError?: boolean;
  fieldError?: string | boolean;
  label?: string;
  disableFuture?: boolean;
  minDate?: any;
  maxDate?: any;
  size?: any;
}

/**
 * Normalize picker / typed values to the app wire format `YYYY-MM-DD`.
 * Never return a Dayjs/Date object — JSON would serialize those as UTC ISO and shift the day.
 */
export const toDateInputString = (value: unknown): string => {
  if (value == null || value === '') return '';

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value.format(identifiers.date_input) : '';
  }

  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format(identifiers.date_input) : '';
  }

  if (typeof value === 'string') {
    const asInput = dayjs(value, identifiers.date_input, true);
    if (asInput.isValid()) return asInput.format(identifiers.date_input);

    const asDisplay = dayjs(value, identifiers.date_format, true);
    if (asDisplay.isValid()) return asDisplay.format(identifiers.date_input);

    // ISO / other parseable strings — use local calendar day, not UTC day
    const loose = dayjs(value);
    return loose.isValid() ? loose.format(identifiers.date_input) : '';
  }

  return '';
};

/** Accept Dayjs, YYYY-MM-DD, or DD/MM/YYYY from any screen without changing call sites. */
const toPickerDayjs = (value: unknown): Dayjs | null => {
  if (value == null || value === '') return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  if (typeof value === 'string') {
    const asInput = dayjs(value, identifiers.date_input, true);
    if (asInput.isValid()) return asInput;

    const asDisplay = dayjs(value, identifiers.date_format, true);
    if (asDisplay.isValid()) return asDisplay;

    const loose = dayjs(value);
    return loose.isValid() ? loose : null;
  }

  return null;
};

const DateInput: FC<DateInputProps & Omit<DesktopDatePickerProps<any, any>, 'renderInput'> & {
  renderInput?: DesktopDatePickerProps<any, any>['renderInput'];
}> = ({
  hasError,
  validators,
  textChange,
  field,
  value,
  placeholder,
  fieldError,
  disabled,
  disableFuture,
  minDate,
  maxDate,
  label,
  typeValue,
  size = 'small',
  PopperProps,
  ...rest
}) => {
  const theme = useTheme();
  const selectedlangLocaleCode = useSelector(defaultLanguage).value.split('_')[0];
  const [error, setError] = useState<{
    error?: boolean;
    message?: string;
  } | null>({ error: hasError, message: fieldError as string });

  const pickerValue = useMemo(() => toPickerDayjs(value), [value]);

  const handleChange = (next: Dayjs | null) => {
    if (!next) return;
    const formatted = toDateInputString(next);
    if (!formatted) return;
    const inputError = validateInput(validators as FormValidators[], formatted);
    textChange(formatted, field as string, inputError);
    if (inputError.error) {
      setError(inputError);
    } else {
      setError(null);
    }
  };

  const onError = (pickerError: any, next: any) => {
    const fomattedMinDate = minDate
      ? minDate.format(identifiers.date_format)
      : dayjs('1900-01-01').format(identifiers.date_format);
    const fomattedMaxDate = maxDate
      ? maxDate.format(identifiers.date_format)
      : dayjs('2099-12-12').format(identifiers.date_format);
    let message = '';
    if (pickerError === 'minDate') {
      message = strings.formatString(strings.min_date_error, fomattedMinDate) as string;
    } else if (pickerError === 'maxDate') {
      message = strings.formatString(strings.max_date_error, fomattedMaxDate) as string;
    } else if (pickerError != null) {
      message = strings.date_format_error;
    }
    const errorMsg = { error: pickerError != null, message };
    setError(errorMsg);

    const formatted = toDateInputString(next);
    if (formatted) {
      // Valid calendar date (may still fail min/max) — emit YYYY-MM-DD
      textChange(formatted, field as string, errorMsg);
      return;
    }

    if (pickerError == null) {
      textChange('', field as string, errorMsg);
      return;
    }

    // Transient invalid typing: keep current controlled value as YYYY-MM-DD string.
    // Avoids wiping parent state and never emits Dayjs/ISO.
    textChange(toDateInputString(value), field as string, errorMsg);
  };

  const customMessage = () => (error && hasError && fieldError ? strings.requiredText : '');
  return (
    <LocalizationProvider adapterLocale={selectedlangLocaleCode} dateAdapter={AdapterDayjs}>
      <DesktopDatePicker
        {...rest}
        data-testid="datepicker"
        views={['year', 'month', 'day']}
        label={label}
        inputFormat={identifiers.date_format}
        value={pickerValue}
        onChange={(newValue: Dayjs | null) => handleChange(newValue)}
        disabled={disabled}
        className={`input-field ${fieldError}`}
        OpenPickerButtonProps={{ color: 'primary' }}
        disableFuture={disableFuture}
        minDate={minDate || null}
        maxDate={maxDate || null}
        onError={onError}
        PopperProps={{
          ...PopperProps,
          sx: { zIndex: theme.zIndex.modal + 3, ...(PopperProps as any)?.sx },
        }}
        renderInput={params => (
          <TextField
            {...params}
            size={size}
            helperText={error && error.error ? error.message : customMessage()}
            error={error?.error || (hasError && fieldError) ? true : false}
          />
        )}
      />
    </LocalizationProvider>
  );
};

export default DateInput;
